/**
 * Easy steps: every procedure, followable by anyone (docs/EASY.md). First principles:
 *   A1 (SELF-EVIDENT) A person holds one instruction at a time, and less under stress. → One action per screen; nothing
 *      to remember from one screen to the next.
 *   A2 (MEASURED: W3C "Making Content Usable for People with Cognitive and Learning Disabilities", 2021; Easy Read
 *      practice) Short sentences of common words, one idea each, a picture per step, concrete actions instead of
 *      abstract numbers, help always in reach. → the lint rules below (E1–E5).
 *   A3 (DERIVED) When something happens, what protects people comes first. → step 1 of every "something happened"
 *      procedure is the safety action (stop, move away, call).
 * Facts come only from the existing sourced content (incidents.ts, sanitize.ts, howto.ts, surfaces.ts); Easy steps
 * re-say them in plain words. They never add a rule.
 */

import { EASY_UT } from "./states/ut/easy.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { cite, wordsFor } from "./states/words.ts";
import { stateOrDefault } from "./states/index.ts";

const WORDS: Record<UsState, StateWords<typeof EASY_UT>> = perStateTable("EASY", EASY_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/** A small picture vocabulary both apps can draw (each app maps these to its own icons). */
export type EasyIcon =
  | "stop" | "people" | "phone" | "hands" | "gloves" | "trash" | "water" | "soap" | "spray" | "wait" | "check" | "bug"
  | "chemical" | "bandage" | "sick" | "allergy" | "fridge" | "thermo" | "food" | "box" | "restroom" | "wrench" | "power"
  | "clean" | "towel" | "sign" | "write" | "camera" | "fire";

export interface EasyStep {
  icon: EasyIcon;
  /** One action, verb first, ≤ 12 words (E1–E3). */
  say: string;
  /** "Wait 10 seconds" gets a timer button instead of asking anyone to count (E4). */
  waitSeconds?: number;
  /** A yes/no to check before going on ("Is the strip the right color?"). No → `ifNo`. */
  check?: { ask: string; ifNo: string };
  /** A "sanitize" step links to the Learn how guide for this situation (sanitize.ts). */
  learnSanitize?: import("./sanitize.ts").SanitizeCase;
  /** Call the person in charge from this step (a big phone button). */
  callManager?: boolean;
  /** What this step's guide uses (things.ts opNeeds): its buy link reads the step's words in this light (products.ts P8). */
  needs?: import("./products.ts").ProductPurposeId[];
  /**
   * What this step uses that its words don't name ("Mix 1/4 cup Barbicide into 4 cups of water" uses a measuring cup): each
   * is on "What you'll need" and gets this step's chip (a buy link and "Where is it?"). Never a thing the step forbids
   * (products.ts forbiddenIn; test/guide-needs.test.ts). At most 2 chips show on a step; the list keeps them all.
   */
  buys?: import("./products.ts").ProductPurposeId[];
  /** The equipment maker's "do not use" lines for this step, each naming the maker (makersupplies.ts M5): drawn as stop lines. */
  stops?: { kind: "stop" | "note" | "caution" | "mix" | "label"; say: string; by: string; row: string; url?: string; quote?: string }[];
  /** Why this step matters, one short line (onboarding v2: "an ontological reason why"). */
  why?: string;
  /** Where this step comes from: research citations ("grooming-and-guests GS2"), or "advice" (shown as "Our advice"). */
  cite?: string[] | "advice";
  /**
   * A CHOICE step: the guide offers ways to do the job ("How do you want to clean the sponge?"). Only the steps whose
   * `only` is the chosen option's key run after it. One mechanism for every branch (the fridge's Yes / No, the sponge's
   * microwave / dishwasher / replace). An option with `requires` shows only when the home has that appliance.
   */
  choose?: { ask: string; options: { key: string; label: string; requires?: "dishwasher" | "microwave" }[] };
  /** Runs only when this option of the guide's choice step was picked. */
  only?: string;
}

/**
 * A "never / don't / avoid" line, or a stop-sign line that is not itself an action ("Stop serving" is an action).
 * Warnings come BEFORE the first action step, so nobody reads "never" after they already did it (owner, 2026-10-05).
 */
export const WARNING_START = /^(never|don'?t|do not|avoid)\b/i;
export const isWarningStep = (st: Pick<EasyStep, "icon" | "say">): boolean => {
  const t = st.say.trim();
  return WARNING_START.test(t) || (st.icon === "stop" && /^keep\b.*\bout of\b/i.test(t));
};
/** Wording that offers another way to do the same job: it must be a choice branch, never a sequential step. */
export const ALTERNATIVE_WORDS = /another way|alternatively|or you can|\binstead\b|^or\b|if you don'?t have|if you have no/i;

/** Warnings first (a stable partition of the steps that run for every choice); choice and branch steps keep their order. */
export function warningsFirst<T extends Pick<EasyStep, "icon" | "say" | "only">>(steps: readonly T[]): T[] {
  const warn = steps.filter((x) => !x.only && isWarningStep(x));
  const rest = steps.filter((x) => x.only || !isWarningStep(x));
  return [...warn, ...rest];
}

/** The steps that run for a choice made (`chosen` undefined: the steps up to and including the choice step). */
export function stepsForChoice<T extends Pick<EasyStep, "choose" | "only">>(steps: readonly T[], chosen?: string): T[] {
  const at = steps.findIndex((x) => x.choose);
  if (at < 0) return [...steps];
  if (chosen === undefined) return steps.slice(0, at + 1);
  return steps.filter((x, i) => i <= at || !x.only || x.only === chosen);
}

/** The warnings that open a guide: shown together as "Before you start", then step 1. */
export function leadingWarnings<T extends Pick<EasyStep, "icon" | "say">>(steps: readonly T[]): T[] {
  const out: T[] = [];
  for (const x of steps) { if (!isWarningStep(x)) break; out.push(x); }
  return out;
}

export interface EasyProcedure {
  id: string;
  /** ≤ 6 words: "Someone threw up". */
  title: string;
  /** What this is for, one short sentence the person can hear first. */
  why: string;
  /** Opened from: an incident type, a check template, a sanitize case, a surface. */
  for: { incident?: string; templateId?: string; sanitize?: string; surfaceId?: string };
  steps: EasyStep[];
  citations: string[];
  /** The equipment's brand and model family, when its record names them (things.ts opPlayer): its supplies are the maker's picks first. */
  maker?: { brand?: string; family?: string; material?: string };
  /** What the item is made of (compat.ts MatId): a chemical on a step shows its warning for each (compat.ts). Set by the guide's builder; absent = read from the words. */
  materials?: string[];
  /** A gross job's "You've got this" opener, shown before step 1 (grossjobs.ts). */
  opener?: import("./grossjobs.ts").Opener;
}

/* ───────── the plain-words rules ───────── */

/** Words that need a plain replacement (E2). Key: the word; value: what to say instead. */
export const PLAIN_WORDS: Record<string, string> = {
  ppm: "the strip color", tcs: "food that must stay cold or hot", pic: "the person in charge", sds: "the chemical's safety sheet",
  "corrective action": "what you did", "contact time": "wait time", discard: "throw away", dispose: "throw away", ventilate: "open doors and windows",
  "epinephrine auto-injector": "EpiPen", epinephrine: "EpiPen", exclude: "go home", restrict: "no food work", ensure: "make sure", utilize: "use",
  approximately: "about", per: "each", potentially: "maybe", hazardous: "dangerous", "cross-contamination": "spreading germs", "cross-contact": "allergy food touching other food",
  immediately: "now", "prior to": "before", sufficient: "enough", adequate: "enough", commence: "start", terminate: "stop", verify: "check", monitor: "watch",
  consume: "eat", notify: "tell", obtain: "get", remove: "take off", subsequently: "then", "in order to": "to", "ready-to-eat": "food that won't be cooked",
  "time/temperature": "cold or hot", receptacle: "trash can", solution: "mix", "absorbent powder": "spill powder",
};

/** Verbs a step may start with (imperative, common words) (E3). */
const VERBS = new Set(("add air ask bag bandage be bring call carry change check clean close count cover cut do dry dump empty fill find "
  + "fix get give go hang help hold keep label lay leave let lift look make mix move open pick plan pour press pull push put read "
  + "rinse run save say scrape scrub see send set shake show shut sign spray stand start step stay stir stop sweep take tap tell test "
  + "throw tie turn unplug use wait walk wash wear wet wipe write zip don't never call scoop sanitize soak store swap tag oil preheat degrease restock refill replace report remake drain flush peel serve cook heat cool chill thaw freeze lock plug dip grab clear log fold point hand trash sort sit squirt spread line type mop refreeze blot flush scrape soak rub work lift treat bleach scour print post seal remove").split(" "));

export interface EasyIssue { rule: "E1" | "E2" | "E3" | "E5"; message: string }

/** Checks one Easy step's words. [] means it passes. */
export function lintEasy(text: string): EasyIssue[] {
  const issues: EasyIssue[] = [];
  const words = text.replace(/[“”"]/g, "").split(/\s+/).filter((w) => /[a-z0-9]/i.test(w));
  if (words.length > 12) issues.push({ rule: "E1", message: `Too long: ${words.length} words (12 at most).` });
  const sentences = text.split(/[.!?](\s|$)/).filter((s) => s && s.trim().length > 1);
  if (sentences.length > 2) issues.push({ rule: "E1", message: "More than two short sentences: split it into steps." });
  const low = ` ${text.toLowerCase().replace(/[^a-z0-9/ -]+/g, " ")} `;
  for (const [w, plain] of Object.entries(PLAIN_WORDS)) if (low.includes(` ${w} `)) issues.push({ rule: "E2", message: `Say "${plain}" instead of "${w}".` });
  const first = words[0]?.toLowerCase().replace(/[^a-z']/g, "");
  if (!first || !VERBS.has(first)) issues.push({ rule: "E3", message: `Start with the action (a verb like "Wash", "Call", "Throw"), not "${words[0] ?? ""}".` });
  if (/;|\(|\)/.test(text)) issues.push({ rule: "E5", message: "No semicolons or brackets: one plain sentence." });
  return issues;
}

export function lintProcedure(p: EasyProcedure): { step: number; text: string; issues: EasyIssue[] }[] {
  const out: { step: number; text: string; issues: EasyIssue[] }[] = [];
  const titleWords = p.title.split(/\s+/).length;
  if (titleWords > 6) out.push({ step: 0, text: p.title, issues: [{ rule: "E1", message: `Title too long: ${titleWords} words (6 at most).` }] });
  // A choice step is a question (no verb-first rule); the length is the longest way through (one choice's steps), not all branches.
  p.steps.forEach((s, i) => { const issues = s.choose ? lintEasy(s.say).filter((x) => x.rule !== "E3") : lintEasy(s.say); if (issues.length) out.push({ step: i + 1, text: s.say, issues }); });
  const longest = Math.max(...(p.steps.find((x) => x.choose)?.choose?.options ?? [{ key: undefined as string | undefined }]).map((o) => stepsForChoice(p.steps, o.key).length));
  if (longest > 10) out.push({ step: 0, text: p.title, issues: [{ rule: "E1", message: `${longest} steps: 10 at most. Split it.` }] });
  return out;
}

/** A plain-words score for any instruction text in the app (the whole-app scan): share of lines that pass. */
export function plainScore(lines: string[]): { lines: number; passing: number; issues: { text: string; issues: EasyIssue[] }[] } {
  const issues = lines.map((text) => ({ text, issues: lintEasy(text) })).filter((x) => x.issues.length);
  return { lines: lines.length, passing: lines.length - issues.length, issues };
}

/* ───────── procedures (the first three are the exemplars; every fact below is re-said from the sourced content) ───────── */

const easyProceduresForCache = new Map<UsState, EasyProcedure[]>();
/** Built once per state (callers look these up often). */
export function easyProceduresFor(state?: UsState): EasyProcedure[] {
  const k = stateOrDefault(state);
  let v = easyProceduresForCache.get(k);
  if (!v) { v = buildEasyProceduresFor(k).map((p) => (p.for.incident || p.for.templateId ? p : { ...p, steps: warningsFirst(p.steps) })); /* an emergency keeps people first (the safety action, then the warnings); a log form (templateId) keeps its steps in the form's order, its answers are keyed to the step number */ easyProceduresForCache.set(k, v); }
  return v;
}

function buildEasyProceduresFor(state?: UsState): EasyProcedure[] {
  const w = words(state);
  return [
  {
    id: "vomit", title: "Someone threw up", why: "Throw-up can spread a stomach bug to everyone. Clean it the safe way.",
    for: { incident: "vomit_diarrhea_cleanup" },
    // ONE list for this event (screen analysis 05, fix R3): the report's "Do this first" (incidents.ts firstSteps) is
    // made from these steps, and they are word for word mess.ts messSteps(["vomit"], "floor") (test: easy.test.ts).
    // The bleach strength and the 5 minutes: docs/source/coffee-shop-food-safety-protocol.md "Vomit cleanup".
    steps: [
      { icon: "stop", say: "Stop serving and throw away food near it." },
      { icon: "sign", say: "Put a wet floor sign by it. Keep people away." },
      { icon: "phone", say: "Tell the person in charge. A sick worker stops food work.", callManager: true },
      { icon: "gloves", say: "Put on gloves, a mask and an apron." },
      { icon: "towel", say: "Cover it with paper towels. Scoop it into a bag." },
      { icon: "soap", say: "Wash the spot with soap and water." },
      { icon: "chemical", say: "Mix 1/3 to 1 1/3 cups 6% bleach into 1 gallon water." },
      { icon: "spray", say: "Spray the bleach mix. Leave it wet for 5 minutes.", waitSeconds: 300 },
      { icon: "towel", say: "Dry the floor. Take the sign away only when dry." },
      { icon: "hands", say: "Bag your gloves and apron, then wash your hands." },
    ],
    citations: ["Food Code 2-501.11", "Food Code 2-201.12", "OSHA 29 CFR 1910.145(c)(2)", "OSHA 29 CFR 1910.22(a)(2)", "Source protocols: vomit or diarrhea cleanup"],
  },
  {
    id: "handwash", title: "Wash your hands", why: "Clean hands keep germs out of the food.",
    for: { surfaceId: "hand-sink" },
    steps: [
      { icon: "water", say: "Turn on warm water." },
      { icon: "hands", say: "Wet your hands." },
      { icon: "soap", say: "Put soap on your hands." },
      { icon: "hands", say: "Scrub fronts, backs, fingers and nails.", waitSeconds: 20 },
      { icon: "water", say: "Rinse off all the soap." },
      { icon: "towel", say: "Dry with a paper towel." },
      { icon: "towel", say: "Use the towel to turn off the water." },
    ],
    citations: ["Food Code 2-301.12", "Source protocols: handwashing"],
  },
  {
    id: "sanitize-counter", title: "Clean and sanitize a counter", why: "A counter can look clean and still have germs.",
    for: { sanitize: "counter_produce", surfaceId: "prep-counter" },
    steps: [
      { icon: "box", say: "Take everything off the counter." },
      { icon: "trash", say: "Wipe off crumbs and food." },
      { icon: "soap", say: "Wash it with soapy water." },
      { icon: "water", say: "Wipe it with clean water." },
      { icon: "spray", say: "Spray sanitizer until it is wet.", learnSanitize: "counter_produce" },
      { icon: "wait", say: "Leave it wet. The label may say longer.", waitSeconds: 60 },
      { icon: "check", say: "Let it dry by itself." },
    ],
    citations: ["Food Code 4-602.11", "Food Code 4-703.11", "Source protocols: how to sanitize each surface (Prep tables and counters)"],
  },

  /* ── incidents (incidents.ts firstSteps; docs/source first-aid, allergen and complaint passages) ── */
  {
    id: "pest", title: "I saw a pest", why: "Pests carry germs onto food and supplies.",
    for: { incident: "pest" },
    steps: [
      { icon: "stop", say: "Stop using food and supplies it may have touched." },
      { icon: "stop", say: "Don't touch bats, birds or droppings." },
      { icon: "phone", say: "Tell the person in charge now.", callManager: true },
      { icon: "trash", say: "Cover or throw away food it may have touched." },
      { icon: "bug", say: "Never spray or set out poison yourself." },
      { icon: "spray", say: "Clean and sanitize the spot.", learnSanitize: "counter_produce" },
      { icon: "write", say: "Write down what you saw." },
    ],
    citations: ["Food Code 6-501.111", "Food Code 6-501.112", "Source protocols: if you find pests"],
  },
  {
    id: "temperature", title: "A fridge or warmer is off", why: "Germs grow fast in food between 41°F and 135°F.",
    for: { incident: "temperature" },
    // ONE list for this event (screen analysis 05, fix R3): the report's "Do this first" is made from these steps.
    // Cold: Food Code 3-501.16(A)(2), coffee-shop protocol "Corrective actions" (above 41°F for more than 4 hours or
    // unknown: discard). Hot: Food Code 3-501.16(A)(1) (135°F or above); parish protocol "Corrective actions at CCPs": "Hot food
    // below 135°F: less than 2 hours, reheat to 165°F; unknown or longer, discard" (reheat to 165°F: 3-403.11).
    steps: [
      { icon: "fridge", say: "Move food that must stay cold or hot to a working unit." },
      { icon: "phone", say: "Tell the person in charge now.", callManager: true },
      { icon: "trash", say: "Throw away cold food above 41°F for more than 4 hours." },
      { icon: "trash", say: "Throw away hot food below 135°F for 2 hours or more." },
      { icon: "thermo", say: "Heat hot food that cooled under 2 hours to 165°F." },
      { icon: "trash", say: "Throw it away if you don't know how long." },
      { icon: "wrench", say: "Fix the unit before you use it again." },
      { icon: "write", say: "Write down the reading and what you did." },
    ],
    citations: ["Food Code 3-501.16", "Food Code 3-403.11", "Source protocols: corrective actions (fridge above 41°F)", "Parish protocol: corrective actions at CCPs (hot food below 135°F)"],
  },
  {
    id: "illness", title: "I'm sick", why: "Being sick can spread to food. Thank you for telling us.",
    for: { incident: "illness" },
    steps: [
      { icon: "stop", say: "Stop food work now." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
      { icon: "check", say: "Don't share medical details. Yes or no is enough." },
      { icon: "people", say: "Let the person in charge decide your work today." },
    ],
    citations: ["Food Code 2-201.11", "Food Code 2-201.12", "Food Code 2-201.13"],
  },
  {
    id: "customer-sick", title: "A customer says they got sick", why: "The county needs to know fast, so no one else gets sick.",
    for: { incident: "customer_illness_complaint" },
    steps: [
      { icon: "phone", say: "Tell the person in charge now.", callManager: true },
      { icon: "write", say: "Write their name and how to reach them." },
      { icon: "write", say: "Write what they ate and when." },
      { icon: "write", say: "Write how they feel and when it started." },
      { icon: "stop", say: "Stop serving the food they ate." },
      { icon: "sign", say: "Label what is left \"Do not serve.\"" },
      { icon: "fridge", say: "Keep it in the fridge." },
      { icon: "phone", say: "Call the county health department today." },
    ],
    citations: ["Food Code 2-201.11", "Source protocols: complaint of illness"],
  },
  {
    id: "cut-burn", title: "I got cut or burned", why: "Care for yourself first. No ice or butter on a burn. Then keep blood away from food.",
    for: { incident: "injury_cut_burn" },
    // The person first (the parish and coffee-shop protocols, "Cuts and burns": stop, wash and press a cut, cool a burn 10 to 20
    // minutes, call 911 for heavy bleeding or large burns), then the food ("Throw away any food that blood touched or might
    // have touched"), then the cover (Food Code 2-401.13: a bandage or finger cot and a single-use glove).
    steps: [
      { icon: "stop", say: "Put the knife or hot pan down and step away." },
      { icon: "water", say: "Cool a burn under running water for 10 to 20 minutes.", waitSeconds: 600 },
      { icon: "bandage", say: "Wash a cut and press on it to stop the bleeding." },
      { icon: "phone", say: "Call 911 for heavy bleeding or a large burn." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
      { icon: "trash", say: "Throw away food that blood may have touched." },
      { icon: "spray", say: "Clean and sanitize the surface.", learnSanitize: "counter_produce" },
      { icon: "gloves", say: "Put on a waterproof bandage, then a glove, before going back." },
    ],
    citations: ["Food Code 2-401.13", "Food Code 3-301.11", "Source protocols: cuts and burns"],
  },
  {
    id: "fire", title: "A fire started", why: "People first: get out and call 911. The water, hood and heat steps are our advice; the fire department has the last word.",
    for: { incident: "fire" },
    // People first (the owner's rule): get out, then 911 (NFPA HM24: "everyone is out, the fire department has been called").
    // Then the grease and hood steps, which are OUR ADVICE (no docs/source line sets them). The extinguisher step is for a small
    // fire only, with the exit at your back, and PASS is USFA's (HM23). No step ever tells anyone to use water; the one line with
    // the word says never (test: firesafety.test.ts F7).
    steps: [
      { icon: "people", say: "Get everyone out of the building now." },
      { icon: "phone", say: "Call 911 once you are outside." },
      { icon: "stop", say: "Never put water on a grease or oil fire." },
      { icon: "fire", say: "Pull the hood fire system handle on your way out." },
      { icon: "fire", say: "Use an extinguisher only on a small fire.", check: { ask: "Is it small and not growing, with the exit behind you?", ifNo: "Do not fight it. Leave now and wait for the fire department." } },
      { icon: "fire", say: "Use PASS: pull the pin, aim low, squeeze, sweep." },
      { icon: "people", say: "Stay outside and count everyone." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
    ],
    citations: ["USFA and NFPA: fire extinguishers (PASS; small fire only; everyone out; fire department called)", ...(w.fireCitation ? [w.fireCitation] : [])],
  },
  {
    id: "equipment", title: "Something is broken", why: "Broken equipment can make food unsafe.",
    for: { incident: "equipment_failure" },
    steps: [
      { icon: "stop", say: "Stop using it." },
      { icon: "sign", say: "Tag it so no one uses it." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
      { icon: "check", say: "Check if it is the dishwasher.", check: { ask: "Is the dishwasher hot and sanitizing?", ifNo: "Use the three-compartment sink for dishes." } },
    ],
    citations: ["Source protocols: dishwashers (temperature or sanitizer low: switch to the three-compartment sink)"],
  },
  {
    id: "outage", title: "The water or power is out", why: "Without water or cold, food can get unsafe fast.",
    for: { incident: "water_power_outage" },
    steps: [
      { icon: "stop", say: "Stop serving food if the water is off." },
      { icon: "stop", say: "Stop if there is a boil-water notice." },
      { icon: "stop", say: "Stop making drinks and ice until the water is cleared." },
      // Handwashing needs water (Food Code 5-202.12(A): the hand sink gives water at least 85°F; Utah adopts the 2022 Food Code, R392-100-4).
      // "No food work where hands cannot be washed" is OUR ADVICE: docs/source has no Utah establishment rule for a water outage (only
      // 26B-7-416(9)(n) for a home kitchen and R392-102-16(7) for a truck), so none is cited for it (core waterout.ts).
      { icon: "hands", say: "Stop food work where hands cannot be washed." },
      { icon: "phone", say: "Tell the person in charge now.", callManager: true },
      { icon: "fridge", say: "Keep fridge and freezer doors shut." },
      { icon: "thermo", say: "Write down the fridge temperatures." },
      { icon: "trash", say: "Throw away cold food above 41°F for more than 4 hours." },
      { icon: "water", say: "Check the hand sink water once the water is back." },
    ],
    citations: ["Food Code 3-501.16", "Food Code 5-202.12(A): hand sink water at least 85°F", "Source protocols: backflow protection (boil-water notice)", "Source protocols: occasions for temperature abuse (power outage)", `Our advice: no food work where hands cannot be washed${w.noHandwashRuleNote ? ` (${w.noHandwashRuleNote})` : ""}`],
  },
  {
    id: "chemical-spill", title: "A chemical spilled", why: "Some chemicals hurt eyes, skin and lungs.",
    for: { incident: "chemical_spill" },
    steps: [
      { icon: "people", say: "Move people and food away." },
      // Eyes or skin splashed: rinse 15 minutes. Source: docs/source/coffee-shop-food-safety-protocol.md "Safe chemical use":
      // "rinse eyes or skin 15 minutes if splashed and call Poison Control at 1-800-222-1222." The sheet's first-aid part is
      // OSHA 29 CFR 1910.1200(g) (the SDS has 16 sections); the app reads no more into either.
      { icon: "water", say: "Rinse eyes or skin with water for 15 minutes.", waitSeconds: 900 },
      { icon: "phone", say: "Call Poison Control: 1-800-222-1222." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
      { icon: "chemical", say: "Open doors and windows." },
      { icon: "chemical", say: "Read part 6 of the chemical's safety sheet for spills." },
      { icon: "stop", say: "Never mix chemicals." },
    ],
    citations: ["Food Code 7-201.11", "OSHA 29 CFR 1910.1200", "Source protocols: safe chemical use", "Source protocols: safety data sheets"],
  },
  {
    id: "allergy", title: "Someone has an allergic reaction", why: "A bad reaction can stop breathing. Get help fast.",
    for: { incident: "allergen_reaction" },
    steps: [
      { icon: "phone", say: "Call 911 for trouble breathing or swelling." },
      { icon: "allergy", say: "Help them use their EpiPen." },
      { icon: "people", say: "Stay with them." },
      { icon: "people", say: "Keep them lying down, or sitting if breathing is hard." },
      { icon: "stop", say: "Give them nothing else to eat or drink." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
      { icon: "food", say: "Keep the dish and its ingredient list." },
    ],
    citations: ["Food Code 3-602.12(C)", "FALCPA", "Source protocols: severe reaction response"],
  },
  {
    id: "contamination", title: "Food got dirty", why: "Dirty food or bare hands can spread germs.",
    for: { incident: "contamination" },
    steps: [
      { icon: "trash", say: "Throw away the food." },
      { icon: "hands", say: "Wash your hands." },
      { icon: "gloves", say: "Change your gloves." },
      { icon: "spray", say: "Clean and sanitize the surface.", learnSanitize: "counter_produce" },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
    ],
    citations: ["Food Code 3-301.11", "Food Code 4-602.11"],
  },
  {
    // The general case of mess.ts (messSteps composes the exact steps once you say what it is and what it's on).
    id: "mess", title: "Something is dirty or spilled", why: "Clean it in the right order, so nobody slips.",
    for: { incident: "mess" },
    steps: [
      { icon: "sign", say: "Put a wet floor sign by it. Keep people away." },
      { icon: "gloves", say: "Put on gloves." },
      { icon: "towel", say: "Soak up anything wet with paper towels." },
      { icon: "clean", say: "Sweep up the dry bits before any water." },
      { icon: "soap", say: "Wash the spot with soapy water." },
      { icon: "towel", say: "Dry the floor. Take the sign away only when dry." },
      { icon: "phone", say: "Tell the person in charge if you can't fix it.", callManager: true },
    ],
    citations: ["OSHA 29 CFR 1910.22(a)(3)", "OSHA 29 CFR 1910.22(a)(2)", "OSHA 29 CFR 1910.145(c)(2)"],
  },
  {
    id: "other", title: "Something else happened", why: "Tell us, so someone can help.",
    for: { incident: "other" },
    steps: [
      { icon: "write", say: "Tell us what happened." },
      { icon: "camera", say: "Take a photo of it." },
      { icon: "phone", say: "Tell the person in charge.", callManager: true },
    ],
    citations: ["CleanCleaver incident report"],
  },

  /* ── sanitize guides (sanitize.ts SANITIZE_GUIDES steps) ── */
  {
    id: "sanitize-counter-raw", title: "Clean up after raw meat", why: "Raw meat juice has germs. The next food may not be cooked.",
    for: { sanitize: "counter_raw" },
    steps: [
      { icon: "food", say: "Put the raw meat away or on a tray." },
      { icon: "box", say: "Take the board and knife to the dish sink." },
      { icon: "trash", say: "Wipe up juice with a paper towel, then throw it away." },
      { icon: "soap", say: "Wash the counter with soapy water and a fresh cloth." },
      { icon: "water", say: "Rinse with clean water." },
      { icon: "spray", say: "Spray sanitizer until it is wet.", learnSanitize: "counter_raw" },
      { icon: "wait", say: "Leave it wet. The label may say longer.", waitSeconds: 60 },
      { icon: "check", say: "Let it dry by itself." },
      { icon: "hands", say: "Wash your hands and change gloves before touching salad or bread." },
    ],
    citations: ["Food Code 4-602.11", "Food Code 3-302.11(A)(2)", "Food Code 4-501.114", "Source protocols: prep order: timing to prevent cross-contamination"],
  },
  {
    id: "sanitize-cutting-board", title: "Clean a plastic cutting board", why: "Knife grooves hold food and germs.",
    for: { sanitize: "cutting_board" },
    steps: [
      { icon: "trash", say: "Scrape off food." },
      { icon: "soap", say: "Wash it in the first sink with hot soapy water." },
      { icon: "clean", say: "Scrub the grooves with a brush." },
      { icon: "water", say: "Rinse it in the second sink." },
      { icon: "spray", say: "Put it all the way into the third sink.", learnSanitize: "cutting_board" },
      { icon: "wait", say: "Leave it in. The label may say longer.", waitSeconds: 60 },
      { icon: "check", say: "Stand it on edge in a rack to dry." },
      { icon: "check", say: "Look at the grooves.", check: { ask: "Can you scrub the grooves clean?", ifNo: "Get a new board." } },
    ],
    citations: ["Food Code 4-501.114", "Food Code 4-101.11", "Source protocols: how to sanitize each surface (Cutting boards)"],
  },
  {
    id: "sanitize-wood-board", title: "Clean a wood board", why: "Wood cracks if it soaks, and cracks hold germs.",
    for: { sanitize: "wood_board" },
    steps: [
      // A wood board never gets plastic-board advice (owner, 2026-10-05): no dish machine, no soak or dip, and oil it.
      // The maker's words: dishwasher-and-dishes D46 (John Boos FAQ).
      { icon: "trash", say: "Scrape off food.", why: "Food left on keeps the soap from reaching the wood.", cite: "advice" },
      { icon: "soap", say: "Wash it with hot soapy water and a brush.", why: "Scrubbing lifts food out of the grain.", cite: "advice" },
      { icon: "stop", say: "Don't let it soak.", why: "Soaked wood splits, and cracks hold germs.", cite: ["dishwasher-and-dishes D46"] },
      { icon: "water", say: "Rinse it fast with clean water.", why: "Water that sits soaks into the wood.", cite: ["dishwasher-and-dishes D46"] },
      { icon: "spray", say: "Spray sanitizer on both sides until wet.", learnSanitize: "wood_board", why: "A spray sanitizes it without a soak.", cite: ["dishwasher-and-dishes D46"] },
      { icon: "wait", say: "Leave it wet. The label may say longer.", waitSeconds: 60, why: "Sanitizer needs its full time to kill germs.", cite: "advice" },
      { icon: "check", say: "Stand it on edge to dry.", why: "Air reaches both sides, so it dries evenly.", cite: ["dishwasher-and-dishes D46"] },
      { icon: "stop", say: "Never put it in the dish machine.", why: "Its maker says wood boards are not dishwasher safe.", cite: ["dishwasher-and-dishes D46"] },
      { icon: "check", say: "Look for deep cuts or cracks.", check: { ask: "Is it free of deep cuts and cracks?", ifNo: "Get a new board." }, why: "Deep cuts hold germs a wash can't reach.", cite: ["home-food-safety H22"] },
      { icon: "clean", say: "Oil it with mineral oil when it looks dry.", why: "Oil keeps the wood from drying out and cracking.", cite: ["dishwasher-and-dishes D46"] },
    ],
    citations: ["Food Code 4-501.114", "Food Code 4-101.17", "Source protocols: how to sanitize each surface (Cutting boards)"],
  },
  {
    id: "sanitize-dishes", title: "Wash dishes in three sinks", why: "Wash, rinse, sanitize, dry. Always in that order.",
    for: { sanitize: "dishes" },
    steps: [
      { icon: "trash", say: "Scrape food into the trash." },
      { icon: "soap", say: "Wash in sink 1 with soapy water, 110°F or hotter." },
      { icon: "water", say: "Rinse in sink 2 with clean water." },
      { icon: "check", say: "Test sink 3 with a strip.", check: { ask: "Is the strip the right color?", ifNo: "Change the water, then test again." } },
      { icon: "spray", say: "Put the dishes all the way under in sink 3.", learnSanitize: "dishes" },
      { icon: "wait", say: "Leave them under. The label may say longer.", waitSeconds: 60 },
      { icon: "check", say: "Set them upside down on a clean rack to dry." },
      { icon: "check", say: "Point utensil handles up." },
      { icon: "water", say: "Change the water when it gets greasy or cool." },
    ],
    citations: ["Food Code 4-301.12", "Food Code 4-501.114", "Source protocols: printable sign: how to use the three-compartment sink"],
  },
  {
    id: "sanitize-equipment", title: "Clean a slicer or mixer", why: "Blades and gaps hide food. Take it apart so you reach it all.",
    for: { sanitize: "stainless_equipment" },
    steps: [
      { icon: "power", say: "Unplug it." },
      { icon: "wrench", say: "Take it apart the way the maker says." },
      { icon: "trash", say: "Scrape or wipe off food." },
      { icon: "soap", say: "Wash the parts in the dish sink." },
      { icon: "soap", say: "Wash parts that don't come off with soapy water." },
      { icon: "water", say: "Rinse with clean water." },
      { icon: "spray", say: "Sanitize every part that touches food.", learnSanitize: "stainless_equipment" },
      { icon: "wait", say: "Leave it wet. The label may say longer.", waitSeconds: 60 },
      { icon: "check", say: "Let it dry, then put it back together." },
    ],
    citations: ["Food Code 4-602.11", "Food Code 4-501.114", "Source protocols: shopping list (Stainless steel)"],
  },
  {
    id: "sanitize-grill", title: "Clean a grill", why: "Heat kills the germs. Scraping takes off old grease and food.",
    for: { sanitize: "grill" },
    steps: [
      { icon: "power", say: "Heat the grill to burn off old food.", learnSanitize: "grill" },
      { icon: "clean", say: "Scrape it with a grill brush or scraper." },
      { icon: "clean", say: "Oil the grates or flat-top." },
      { icon: "check", say: "Look for loose brush bristles.", check: { ask: "Is it free of loose bristles?", ifNo: "Pick them off before you cook." } },
      { icon: "stop", say: "Don't spray bleach or sanitizer on a hot grill." },
      { icon: "clean", say: "Clean the table next to it like a counter.", learnSanitize: "counter_raw" },
      { icon: "check", say: "Clean the grill at least every 24 hours." },
    ],
    citations: ["Food Code 4-602.12", "Source protocols: how to sanitize each surface (Grill grates)", "Source protocols: how to sanitize each surface (Grill-side tables)"],
  },
  {
    id: "sanitize-glass", title: "Clean a display case", why: "Glass cleaner is for glass only, never for what food touches.",
    for: { sanitize: "glass" },
    steps: [
      { icon: "box", say: "Take out the food and trays." },
      { icon: "soap", say: "Wash the trays and shelves food touches." },
      { icon: "water", say: "Rinse them with clean water." },
      { icon: "spray", say: "Sanitize them like a counter.", learnSanitize: "glass" },
      { icon: "check", say: "Let them dry by themselves." },
      { icon: "spray", say: "Use glass cleaner on the outside glass and windows." },
      { icon: "stop", say: "Never put glass cleaner on trays or shelves." },
      { icon: "food", say: "Put food back only when everything is dry." },
    ],
    citations: ["Food Code 4-501.114", "Food Code 7-204.11", "Source protocols: shopping list (Glass, mirrors, windows, display cases)"],
  },
  {
    id: "sanitize-stone", title: "Clean a stone counter", why: "Bleach and acids hurt stone.",
    for: { sanitize: "stone" },
    steps: [
      { icon: "box", say: "Clear the counter and wipe off food." },
      { icon: "soap", say: "Clean with stone cleaner or mild soapy water." },
      { icon: "water", say: "Wipe it with a clean, wet cloth." },
      { icon: "spray", say: "Spray a sanitizer that says it is safe for stone.", learnSanitize: "stone" },
      { icon: "wait", say: "Leave it wet for the time on the label." },
      { icon: "check", say: "Let it dry by itself." },
      { icon: "stop", say: "Never use bleach, vinegar or lemon on stone." },
    ],
    citations: ["Food Code 7-204.11", "Source protocols: shopping list (Granite and stone counters)"],
  },
  {
    id: "sanitize-touch-points", title: "Wipe handles and switches", why: "Everyone touches these, so germs pass through them.",
    for: { sanitize: "touch_points" },
    steps: [
      { icon: "clean", say: "Wipe off dirt you can see." },
      { icon: "spray", say: "Wipe or spray with disinfectant until wet.", learnSanitize: "touch_points" },
      { icon: "wait", say: "Leave it wet for the time on the label." },
      { icon: "check", say: "Let it dry." },
      { icon: "stop", say: "Never use these wipes on food counters or dishes." },
      { icon: "clean", say: "Do this every few hours when busy, and at close." },
    ],
    citations: ["Source protocols: how to sanitize each surface (Door handles, cooler lids, light switches, faucet handles)"],
  },

  /* ── checks (howto.ts HOW_TO and sanitizerHowTo; surfaces.ts contingencies) ── */
  {
    id: "test-sanitizer", title: "Test the sanitizer", why: "Too weak and germs live. Too strong and it gets on food.",
    for: { templateId: "sanitizer" },
    steps: [
      { icon: "chemical", say: "Mix the sanitizer with water the way the label says." },
      { icon: "check", say: "Dip one strip that matches your sanitizer. Time it by the vial." },
      { icon: "check", say: "Hold it next to the color chart on the vial." },
      { icon: "check", say: "Find the closest color.", check: { ask: "Is it the right color for your sanitizer?", ifNo: "Add sanitizer if weak, water if strong. Test again." } },
      { icon: "write", say: "Type the number you read." },
      { icon: "box", say: "Close the vial so the strips stay dry." },
    ],
    citations: ["Food Code 4-302.14", "Food Code 4-501.114", "Source protocols: testing strength"],
  },
  {
    id: "fridge-temp", title: "Check the fridge", why: "Germs grow fast above 41°F.",
    for: { templateId: "cold_units" },
    steps: [
      { icon: "thermo", say: "Read the thermometer in the middle, not by the door." },
      { icon: "fridge", say: "Check the fridge.", check: { ask: "Is it 41°F or colder?", ifNo: "Move the food to a fridge that works." } },
      { icon: "fridge", say: "Check the freezer.", check: { ask: "Is it 0°F or colder?", ifNo: "Tell the person in charge now." } },
      { icon: "write", say: "Type each temperature." },
      { icon: "phone", say: "Tell the person in charge if one is too warm.", callManager: true },
      { icon: "trash", say: "Throw away food above 41°F for more than 4 hours." },
      { icon: "write", say: "Write what you did." },
    ],
    citations: ["Food Code 3-501.16", "Source protocols: corrective actions (fridge above 41°F)"],
  },
  {
    id: "calibrate-thermometer", title: "Check the thermometer", why: "Every temperature is only as true as the thermometer.",
    for: { templateId: "calibration" },
    steps: [
      { icon: "water", say: "Fill a glass with crushed ice." },
      { icon: "water", say: "Add cold water to the top." },
      { icon: "wait", say: "Stir it, then wait.", waitSeconds: 30 },
      { icon: "thermo", say: "Put the probe in the middle of the glass." },
      { icon: "stop", say: "Keep it off the sides and bottom." },
      { icon: "check", say: "Read it.", check: { ask: "Does it read 32°F?", ifNo: "Fix it as the maker says, or use another. Test again." } },
      { icon: "stop", say: `Don't use boiling water to check it${w.noBoilingStepSuffix ?? ""}.` },
    ],
    citations: ["Food Code 4-203.11", cite("Source protocols: thermometer calibration, ice-point method", w.noBoilingCitationNote)],
  },
  {
    id: "restroom-check", title: "Check the restroom", why: "A stocked sink lets everyone wash their hands.",
    for: { templateId: "restroom_check" },
    steps: [
      { icon: "soap", say: "Check the soap and paper towels.", check: { ask: "Is there soap, and towels or a dryer?", ifNo: "Restock them now." } },
      { icon: "restroom", say: "Check the toilet paper.", check: { ask: "Is there toilet paper?", ifNo: "Restock it now." } },
      { icon: "water", say: "Check for warm water.", check: { ask: "Is the water warm?", ifNo: "Tell the person in charge to get it fixed." } },
      { icon: "clean", say: "Check that it is clean.", check: { ask: "Is it clean?", ifNo: "Clean it now." } },
      { icon: "trash", say: "Check the trash.", check: { ask: "Is the trash not overflowing?", ifNo: "Empty it now." } },
      { icon: "restroom", say: "Check that the door closes by itself.", check: { ask: "Does it close by itself?", ifNo: "Fix it now." } },
      { icon: "write", say: "Write what you fixed." },
      { icon: "stop", say: "Use only the restroom cloths and mop. Never in the kitchen." },
      { icon: "hands", say: "Wash your hands when you finish." },
      { icon: "hands", say: "Wash again at the kitchen sink before touching food." },
    ],
    citations: ["Food Code 6-301.11", "Food Code 6-301.12", "Food Code 6-202.14", "Food Code 6-501.16", "Food Code 5-202.12"],
  },

  /* ── thawing (thaw.ts thawPlan) ── */
  {
    id: "thaw-fridge", title: "Thaw food in the fridge", why: "The fridge is the safest way to thaw.",
    for: {},
    steps: [
      { icon: "box", say: "Keep it in its package, in a pan or on a tray." },
      { icon: "fridge", say: "Put it on the bottom shelf." },
      { icon: "food", say: "Keep it below food that won't be cooked." },
      { icon: "wait", say: "Plan about 1 day for every 4 to 5 pounds." },
      { icon: "write", say: "Label it with the day it went in the fridge." },
      { icon: "thermo", say: "Keep the fridge at 41°F or colder." },
      { icon: "food", say: "Cook it to the right temperature for that food." },
    ],
    citations: ["FDA Food Code 3-501.13", "CleanCleaver food safety protocol, Thawing", "USDA FSIS, The Big Thaw"],
  },
  {
    id: "thaw-water", title: "Thaw food in cold water", why: "Cold running water thaws food fast and safe.",
    for: {},
    steps: [
      { icon: "box", say: "Keep it sealed in a leak-proof bag." },
      { icon: "water", say: "Put it in a clean, sanitized prep sink, never a hand sink.", learnSanitize: "counter_produce" },
      { icon: "water", say: "Keep it all the way under cold running water." },
      { icon: "thermo", say: "Keep the water 70°F or colder." },
      { icon: "water", say: "Let the water run so bits wash over the edge." },
      { icon: "wait", say: "Plan about 30 minutes for each pound." },
      { icon: "fridge", say: "Use the fridge if it would take over 4 hours." },
      { icon: "food", say: "Cook it as soon as it is thawed." },
      { icon: "stop", say: "Don't refreeze it raw." },
      { icon: "hands", say: "Clean and sanitize the sink, then wash your hands.", learnSanitize: "counter_produce" },
    ],
    citations: ["FDA Food Code 3-501.13", "FDA Food Code 3-501.13(B)", "USDA FSIS, The Big Thaw", "CleanCleaver food safety protocol, Thawing"],
  },
  {
    id: "thaw-microwave", title: "Thaw food in the microwave", why: "The microwave is fast, so cook the food right after.",
    for: {},
    steps: [
      { icon: "trash", say: "Take it out of foam trays and plastic wrap." },
      { icon: "food", say: "Put it in a microwave-safe dish." },
      { icon: "power", say: "Use the defrost setting." },
      { icon: "write", say: "Type in the weight if the microwave asks." },
      { icon: "wait", say: "Plan about 7 to 8 minutes for each pound." },
      { icon: "food", say: "Turn the pieces partway so the edges don't cook." },
      { icon: "fridge", say: "Use the fridge for big packs." },
      { icon: "food", say: "Cook it right away. No waiting." },
      { icon: "stop", say: "Don't refreeze it raw." },
    ],
    citations: ["FDA Food Code 3-501.13", "Clemson HGIC, How to Thaw Food Safely", "CleanCleaver food safety protocol, Thawing"],
  },

  /* ── storage and cleaning tools (sop.ts STORAGE_RULES; surfaces.ts kitchen-floor, mop-sink) ── */
  {
    id: "store-chemicals", title: "Put chemicals away", why: "A drip from above lands in food. A bottle with no label gets mixed up.",
    for: {},
    steps: [
      { icon: "chemical", say: "Store chemicals below and away from food." },
      { icon: "box", say: "Keep them away from dishes, utensils, cloths and paper cups." },
      { icon: "stop", say: "Never put them on a shelf above food." },
      { icon: "box", say: "Keep them in the bottle they came in." },
      { icon: "sign", say: "Label every spray bottle and bucket with the chemical's name." },
      { icon: "stop", say: "Never put chemicals in a food or drink container." },
      { icon: "stop", say: "Never put food in a chemical container." },
      { icon: "stop", say: "Never mix bleach with ammonia or acid. It makes poison gas." },
      { icon: "write", say: "Keep the safety sheets in a folder everyone can reach." },
      { icon: "bug", say: "Never spray bug poison yourself." },
    ],
    citations: ["Food Code 7-201.11", "Food Code 7-102.11", "Food Code 7-202.11", "Food Code 7-202.12", "Source protocols: cleaning supplies and chemicals", "Source protocols: safe chemical use", "OSHA 29 CFR 1910.1200"],
  },
  {
    id: "mop-floor", title: "Mop the kitchen floor", why: "Mop water carries whatever was on the floor.",
    for: { surfaceId: "kitchen-floor" },
    steps: [
      { icon: "food", say: "Cover or put away food first." },
      { icon: "clean", say: "Sweep the floor." },
      { icon: "water", say: "Fill the bucket at the mop sink." },
      { icon: "clean", say: "Mop with detergent, or a degreaser for grease." },
      { icon: "water", say: "Dump the dirty water at the mop sink." },
      { icon: "stop", say: "Never dump it in a food, dish or hand sink." },
      { icon: "water", say: "Rinse the bucket." },
      { icon: "towel", say: "Hang the mop to dry where it can't drip on food." },
      { icon: "stop", say: "Never use the restroom mop in the kitchen." },
    ],
    citations: ["Food Code 5-203.13", "Food Code 6-501.16", "Source protocols: master cleaning schedule (Floors (kitchen))", "Source protocols: cleaning supplies and chemicals (mops, brooms and buckets)"],
  },

  /* ── supplies (surfaces.ts hand-sink and restroom-sink contingencies) ── */
  {
    id: "out-of-soap", title: "Out of soap or towels", why: "People can't wash their hands without soap and towels.",
    for: {},
    steps: [
      { icon: "soap", say: "Restock the soap now." },
      { icon: "towel", say: "Restock the paper towels now." },
      { icon: "water", say: "Check for warm water.", check: { ask: "Is the water warm?", ifNo: "Tell the person in charge to get it fixed." } },
      { icon: "write", say: "Write what you did." },
    ],
    citations: ["Food Code 6-301.11", "Food Code 6-301.12", "Food Code 5-202.12"],
  },
  ];
}

export const EASY_PROCEDURES: EasyProcedure[] = easyProceduresFor("UT");

export function easyFor(q: { incident?: string; templateId?: string; sanitize?: string; surfaceId?: string }, state?: UsState): EasyProcedure | undefined {
  const procs = easyProceduresFor(state);
  return procs.find((p) => (q.incident && p.for.incident === q.incident) || (q.templateId && p.for.templateId === q.templateId) || (q.sanitize && p.for.sanitize === q.sanitize))
    ?? (q.surfaceId ? procs.find((p) => p.for.surfaceId === q.surfaceId) : undefined);
}
export const getEasy = (id: string, state?: UsState) => easyProceduresFor(state).find((p) => p.id === id);

/** "Wait 30 seconds" / "Wait 5 minutes": the timer button's words. */
export function waitLabel(seconds: number): string {
  return seconds < 60 ? `Wait ${seconds} seconds` : `Wait ${Math.round(seconds / 60)} minute${seconds >= 120 ? "s" : ""}`;
}
